import { useEffect, useMemo, useState } from "react";
import axios from "axios";

/* ─────────────────────────────────────────────
   ICONS
───────────────────────────────────────────── */
const IconBriefcase = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="7" width="20" height="14" rx="2" />
    <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
    <path d="M2 12h20" />
  </svg>
);
const IconHome = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);
const IconPlane = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.8 19.2L16 11l3.5-3.5A2.12 2.12 0 0 0 16.5 4.5L13 8 4.8 6.2a1 1 0 0 0-.9.3L3 7.5l7 4.7-1.5 1.5-3-.75L4 14l2.5 1.5L8 18l1.5-1.5L9 14l4.7 7 1-.9a1 1 0 0 0 .3-.9z" />
  </svg>
);
const IconPalmtree = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 8c0-2.76-2.46-5-5.5-5S2 5.24 2 8h11z" />
    <path d="M13 7.14A5.82 5.82 0 0 1 16.5 6c3.04 0 5.5 2.24 5.5 5h-9z" />
    <path d="M5.89 9.71c-2.15 2.15-2.3 5.47-.35 7.43l4.24-4.25.7-.7.71-.71 2.12-2.12c-1.95-1.96-5.27-1.8-7.42.35z" />
    <path d="M11 15.5c.5 2.5-.17 4.5-1 6.5h4c2-5.5-.5-12-1-14z" />
  </svg>
);
const IconUsers = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconMapPin = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
function getCurrentFY() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month >= 4) return { start: `${year}-04-01`, end: `${year + 1}-03-31` };
  return { start: `${year - 1}-04-01`, end: `${year}-03-31` };
}
const { start: FY_START, end: FY_END } = getCurrentFY();

const PERIOD_LABEL = { today: "Today", week: "This week", month: "This month", year: "This financial year" };

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
function isWeekend(dateStr) {
  const day = new Date(dateStr + "T00:00:00").getDay();
  return day === 0 || day === 6;
}
function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function getPeriodRange(period) {
  const today = new Date();
  const todayStr = toDateStr(today);
  const fyEnd = todayStr < FY_END ? todayStr : FY_END;
  if (period === "today") return [todayStr, todayStr];
  if (period === "week") {
    const mon = new Date(today);
    mon.setDate(today.getDate() - ((today.getDay() + 6) % 7));
    const start = toDateStr(mon) < FY_START ? FY_START : toDateStr(mon);
    return [start, fyEnd];
  }
  if (period === "month") {
    const start = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
    return [start < FY_START ? FY_START : start, fyEnd];
  }
  return [FY_START, fyEnd];
}

/**
 * Core rule: if ANY login on a given day is "office", entire day = office.
 * Otherwise if logins exist → wfh day.
 */
function buildDaysForPeriod(employeeRecord, period) {
  const { holidays = [], leaves = [], travels = [], logins = [] } = employeeRecord;

  const holidayMap = new Map(holidays.map((h) => [h.date, h.name]));

  const leaveMap = new Map();
  for (const l of leaves) {
    let d = new Date(l.start + "T00:00:00");
    const end = new Date(l.end + "T00:00:00");
    while (d <= end) { leaveMap.set(toDateStr(d), l.type); d.setDate(d.getDate() + 1); }
  }

  const travelSet = new Set();
  for (const t of travels) {
    let d = new Date(t.start + "T00:00:00");
    const end = new Date(t.end + "T00:00:00");
    while (d <= end) { travelSet.add(toDateStr(d)); d.setDate(d.getDate() + 1); }
  }

  // Group logins by date
  const loginMap = new Map();
  for (const l of logins) {
    if (!loginMap.has(l.date)) loginMap.set(l.date, []);
    loginMap.get(l.date).push({ time: l.time, source: l.source });
  }

  const [startStr, endStr] = getPeriodRange(period);
  const result = [];
  let d = new Date(startStr + "T00:00:00");
  const end = new Date(endStr + "T00:00:00");

  while (d <= end) {
    const dateStr = toDateStr(d);
    const weekend = isWeekend(dateStr);
    const holName = holidayMap.get(dateStr);
    const leaveType = leaveMap.get(dateStr);
    const isTravel = travelSet.has(dateStr);
    const segments = loginMap.get(dateStr) || [];

    const hasOfficeLogin = segments.some((s) => s.source === "office");
    const hasAnyLogin = segments.length > 0;

    let status = "workday";
    if (weekend) status = "weekend";
    else if (holName) status = "holiday";
    else if (leaveType) status = "leave";
    else if (isTravel) status = "travel";

    result.push({
      date: dateStr,
      status,
      holidayName: holName,
      leaveType,
      segments,
      weekend,
      hasOfficeLogin,
      hasAnyLogin,
    });

    d.setDate(d.getDate() + 1);
  }
  return result;
}

function computeStats(days) {
  let office = 0, wfh = 0, travel = 0, leave = 0, noActivity = 0, holidays = 0;
  for (const d of days) {
    if (d.status === "leave") { leave++; continue; }
    if (d.status === "travel") { travel++; continue; }
    if (d.status === "holiday") { holidays++; continue; }
    if (d.status === "weekend") continue;
    if (d.hasOfficeLogin) office++;
    else if (d.hasAnyLogin) wfh++;
    else noActivity++;
  }
  return { office, wfh, travel, leave, noActivity, holidays };
}

/* ─────────────────────────────────────────────
   AVATAR
───────────────────────────────────────────── */
const AVATAR_COLORS = [
  ["#e8f5e9","#2e7d32"], ["#e3f2fd","#1565c0"], ["#fce4ec","#c62828"],
  ["#fff3e0","#e65100"], ["#f3e5f5","#6a1b9a"], ["#e0f7fa","#00695c"],
  ["#fff8e1","#f57f17"], ["#fbe9e7","#bf360c"],
];
function getAvatarColors(name = "") {
  const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}
function initials(name = "") {
  const parts = name.trim().split(" ");
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

/* ─────────────────────────────────────────────
   STAT PILL
───────────────────────────────────────────── */
function StatPill({ value, label, tone }) {
  const colors = {
    office: { bg: "#e8faf2", text: "#16a34a", border: "#bbf7d0" },
    wfh:    { bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" },
    travel: { bg: "#fff7ed", text: "#ea580c", border: "#fed7aa" },
    leave:  { bg: "#fdf2f8", text: "#db2777", border: "#fbcfe8" },
    noActivity: {bg: "#f3f4f6",text: "#6b7280",border: "#d1d5db",},
  };
  const c = colors[tone] || colors.office;
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      padding: "6px 10px", borderRadius: 10,
      background: c.bg, border: `1px solid ${c.border}`,
      minWidth: 48,
    }}>
      <span style={{ fontSize: 18, fontWeight: 700, color: c.text, lineHeight: 1 }}>{value}</span>
      <span style={{ fontSize: 10, fontWeight: 600, color: c.text, opacity: 0.7, marginTop: 3, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</span>
    </div>
  );
}

/* ─────────────────────────────────────────────
   EMPLOYEE CARD
───────────────────────────────────────────── */
function EmployeeCard({ emp, period }) {
  const days = useMemo(() => buildDaysForPeriod(emp, period), [emp, period]);
  const stats = useMemo(() => computeStats(days), [days]);

  const hasWorkdays = stats.office + stats.wfh > 0;
  const [bg, fg] = getAvatarColors(emp.fullName);
  const ini = initials(emp.fullName);

  return (
    <div
      style={{
        background: "#fff", borderRadius: 16, padding: "20px 22px",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
        transition: "box-shadow 0.15s",
      }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.10)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.06)"}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: "50%",
          background: bg, color: fg,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700, fontSize: 15, flexShrink: 0,
          fontFamily: "'DM Sans', sans-serif",
        }}>{ini.toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 650, fontSize: 15, color: "#111827", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {emp.fullName}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}>
            {emp.department && <span>{emp.department} · </span>}
            <IconMapPin />
            <span>{emp.worklocationName || "Unknown location"}</span>
          </div>
        </div>
      </div>

      {/* No activity notice */}
      {!hasWorkdays && (
        <p style={{ fontSize: 12, color: "#9ca3af", margin: "0 0 12px", fontStyle: "italic" }}>
          No working days logged in this period.
        </p>
      )}

      {/* Stat pills */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <StatPill value={stats.office} label="Office" tone="office" />
        <StatPill value={stats.wfh}    label="WFH"    tone="wfh" />
        <StatPill value={stats.travel} label="Travel" tone="travel" />
        <StatPill value={stats.leave}  label="Leave"  tone="leave" />
        <StatPill value={stats.noActivity} label="No Login" tone="noActivity" />
      </div>

      {/* Footer meta */}
      {/* {(stats.holidays > 0 || stats.noActivity > 0) && (
        <div style={{ marginTop: 12, display: "flex", gap: 12, fontSize: 12, color: "#9ca3af" }}>
          {stats.holidays > 0 && <span>{stats.holidays} holiday{stats.holidays > 1 ? "s" : ""}</span>}
          {stats.noActivity > 0 && <span>{stats.noActivity} no-activity</span>}
        </div>
      )} */}
    </div>
  );
}

/* ─────────────────────────────────────────────
   AGGREGATE STAT TILE
───────────────────────────────────────────── */
function AggregateTile({ icon: Icon, value, label, tone }) {
  const tones = {
    office: { bg: "#f0fdf4", accent: "#16a34a", iconBg: "#dcfce7" },
    wfh:    { bg: "#eff6ff", accent: "#2563eb", iconBg: "#dbeafe" },
    travel: { bg: "#fff7ed", accent: "#ea580c", iconBg: "#fed7aa" },
    leave:  { bg: "#fdf2f8", accent: "#db2777", iconBg: "#fce7f3" },
    noActivity: {
  bg: "#f9fafb",
  accent: "#6b7280",
  iconBg: "#e5e7eb",
},
  };
  const t = tones[tone];
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
      padding: "18px 20px", display: "flex", alignItems: "center", gap: 14,
      flex: "1 1 160px",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: t.iconBg, color: t.accent,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <Icon />
      </div>
      <div>
        <div style={{ fontSize: 26, fontWeight: 750, color: "#111827", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{label}</div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   LOCATION FILTER PILL
───────────────────────────────────────────── */
function LocationPill({ label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 14px", borderRadius: 999, border: "1.5px solid",
        borderColor: active ? "#111827" : "#e5e7eb",
        background: active ? "#111827" : "#fff",
        color: active ? "#fff" : "#374151",
        fontSize: 13, fontWeight: 550, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 6,
        transition: "all 0.15s",
        whiteSpace: "nowrap",
      }}
    >
      <IconMapPin />
      {label}
      <span style={{
        background: active ? "rgba(255,255,255,0.2)" : "#f3f4f6",
        color: active ? "#fff" : "#6b7280",
        borderRadius: 999, padding: "1px 7px", fontSize: 11, fontWeight: 650,
      }}>{count}</span>
    </button>
  );
}
function StatusPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "6px 14px",
        borderRadius: 999,
        border: "1.5px solid",
        borderColor: active ? "#111827" : "#e5e7eb",
        background: active ? "#111827" : "#fff",
        color: active ? "#fff" : "#374151",
        fontSize: 13,
        fontWeight: 550,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────
   PERIOD TAB
───────────────────────────────────────────── */
function PeriodTab({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "7px 16px", borderRadius: 8, border: "none",
        background: active ? "#fff" : "transparent",
        color: active ? "#111827" : "#6b7280",
        fontSize: 13.5, fontWeight: active ? 650 : 500,
        cursor: "pointer",
        boxShadow: active ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
        transition: "all 0.15s",
      }}
    >{label}</button>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const HrAdminView = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  const [period, setPeriod] = useState("today");
  const [selectedLocationId, setSelectedLocationId] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [apiResponse, setApiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { fetchAttendance(); }, []); // eslint-disable-line

  const fetchAttendance = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(
        `${API_BASE_URL}/employeeinfos/employee/attendance-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("Api Response:",res.data);
      
      setApiResponse(res.data);
    } catch (err) {
      console.error(err);
      setError("Failed to load attendance data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const allEmployees = apiResponse?.data ?? [];

  // Build unique worklocation list from employees (uses actual DB data, no hardcoding)
  const worklocations = useMemo(() => {
    const map = new Map();
    for (const emp of allEmployees) {
      const id = String(emp.worklocationId ?? emp.worklocation_id ?? "unknown");
      const name = emp.worklocationName || "Unknown";
      if (!map.has(id)) map.set(id, { id, name, count: 0 });
      map.get(id).count += 1;
    }
    // Sort alphabetically by name
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allEmployees]);

  // Filter employees by selected worklocation
  const visibleEmployees = useMemo(() => {
  let employees = allEmployees;

  // Location filter
  if (selectedLocationId !== "all") {
    employees = employees.filter((emp) => {
      const id = String(
        emp.worklocationId ?? emp.worklocation_id ?? "unknown"
      );
      return id === selectedLocationId;
    });
  }

  // Attendance filter
  if (selectedStatus !== "all") {
    employees = employees.filter((emp) => {
      const days = buildDaysForPeriod(emp, period);
      const stats = computeStats(days);

      switch (selectedStatus) {
        case "office":
          return stats.office > 0;

        case "wfh":
          return stats.wfh > 0;

        case "travel":
          return stats.travel > 0;

        case "leave":
          return stats.leave > 0;
        case "noActivity":
         return stats.noActivity > 0;

        default:
          return true;
      }
    });
  }

  return employees;
}, [
  allEmployees,
  selectedLocationId,
  selectedStatus,
  period,
]);

  // Aggregate stats across visible employees for the selected period
// Aggregate stats across location-filtered employees only (NOT affected by status filter)
const locationFilteredEmployees = useMemo(() => {
  if (selectedLocationId === "all") return allEmployees;
  return allEmployees.filter((emp) => {
    const id = String(emp.worklocationId ?? emp.worklocation_id ?? "unknown");
    return id === selectedLocationId;
  });
}, [allEmployees, selectedLocationId]);

const aggregateStats = useMemo(() => {
  let office = 0, wfh = 0, travel = 0, leave = 0, noActivity = 0;
  for (const emp of locationFilteredEmployees) {
    const days = buildDaysForPeriod(emp, period);
    const s = computeStats(days);
    office += s.office; wfh += s.wfh; travel += s.travel; leave += s.leave; noActivity += s.noActivity;
  }
  return { office, wfh, travel, leave, noActivity };
}, [locationFilteredEmployees, period]);

  const periodLabel = PERIOD_LABEL[period]?.toLowerCase() ?? period;

  return (
    <div style={{
      minHeight: "100vh", background: "#f9fafb",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    }}>
      {/* ── Header ── */}
      <header style={{
        background: "#fff", borderBottom: "1px solid #e5e7eb",
        padding: "0 28px", height: 56,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <span style={{ fontWeight: 750, fontSize: 17, color: "#111827", letterSpacing: "-0.02em" }}>
          Work<span style={{ color: "#16a34a" }}>Track</span>
        </span>
        <span style={{
          background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0",
          borderRadius: 999, padding: "3px 10px", fontSize: 12, fontWeight: 650,
          display: "flex", alignItems: "center", gap: 5,
        }}>
          <IconUsers /> Admin
        </span>
      </header>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "28px 24px" }}>

        {/* ── Work Location filter ── */}
        <div style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: "#9ca3af",
            letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 10,
          }}>
            Work Location
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {/* All locations pill */}
            <LocationPill
              label="All locations"
              count={allEmployees.length}
              active={selectedLocationId === "all"}
              onClick={() => setSelectedLocationId("all")}
            />
            {/* One pill per unique worklocation from the DB */}
            {worklocations.map((loc) => (
              <LocationPill
                key={loc.id}
                label={loc.name}
                count={loc.count}
                active={selectedLocationId === loc.id}
                onClick={() => setSelectedLocationId(loc.id)}
              />
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 20 }}>
  <div
    style={{
      fontSize: 10,
      fontWeight: 700,
      color: "#9ca3af",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      marginBottom: 10,
    }}
  >
    Attendance Type
  </div>

  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
    <StatusPill
      label="All"
      active={selectedStatus === "all"}
      onClick={() => setSelectedStatus("all")}
    />

    <StatusPill
      label="Office"
      active={selectedStatus === "office"}
      onClick={() => setSelectedStatus("office")}
    />

    <StatusPill
      label="WFH"
      active={selectedStatus === "wfh"}
      onClick={() => setSelectedStatus("wfh")}
    />

    <StatusPill
      label="Travel"
      active={selectedStatus === "travel"}
      onClick={() => setSelectedStatus("travel")}
    />

    <StatusPill
      label="Leave"
      active={selectedStatus === "leave"}
      onClick={() => setSelectedStatus("leave")}
    />
    <StatusPill
      label="No Login"
      active={selectedStatus === "noActivity"}
      onClick={() => setSelectedStatus("noActivity")}
    />
  </div>
</div>

        {/* ── Period tabs ── */}
        <div style={{
          display: "inline-flex", gap: 2, background: "#f3f4f6",
          borderRadius: 10, padding: "3px", marginBottom: 24,
        }}>
          {[
            { value: "today", label: "Today" },
            { value: "week",  label: "Week" },
            { value: "month", label: "Month" },
            { value: "year",  label: "Year" },
          ].map((t) => (
            <PeriodTab key={t.value} label={t.label} active={period === t.value} onClick={() => setPeriod(t.value)} />
          ))}
        </div>

        {/* ── Aggregate stat tiles ── */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
          <AggregateTile icon={IconBriefcase} value={aggregateStats.office} label={`office days ${periodLabel}`} tone="office" />
          <AggregateTile icon={IconHome}      value={aggregateStats.wfh}    label={`WFH days ${periodLabel}`}    tone="wfh" />
          <AggregateTile icon={IconPlane}     value={aggregateStats.travel} label={`travel days ${periodLabel}`} tone="travel" />
          <AggregateTile icon={IconPalmtree}  value={aggregateStats.leave}  label={`leave days ${periodLabel}`}  tone="leave" />
          <AggregateTile icon={IconUsers}value={aggregateStats.noActivity}label={`no login days ${periodLabel}`} tone="noActivity"/>
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 60, color: "#6b7280" }}>
            <div style={{
              width: 32, height: 32, border: "3px solid #e5e7eb",
              borderTopColor: "#16a34a", borderRadius: "50%",
              animation: "spin 0.7s linear infinite", margin: "0 auto 12px",
            }} />
            Loading attendance data…
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {!loading && error && (
          <div style={{ textAlign: "center", padding: 40 }}>
            <p style={{ color: "#ef4444", marginBottom: 12 }}>{error}</p>
            <button onClick={fetchAttendance} style={{
              padding: "8px 20px", borderRadius: 8, border: "none",
              background: "#111827", color: "#fff", fontWeight: 600, cursor: "pointer",
            }}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ── People header ── */}
            <div style={{
              fontSize: 11, fontWeight: 700, color: "#9ca3af",
              letterSpacing: "0.1em", textTransform: "uppercase",
              marginBottom: 14, display: "flex", alignItems: "center", gap: 6,
            }}>
              <IconUsers />
              People ({visibleEmployees.length} of {allEmployees.length})
            </div>

            {/* ── Employee grid ── */}
            {visibleEmployees.length === 0 ? (
              <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
                No employees in this location.
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                gap: 16,
              }}>
                {visibleEmployees.map((emp) => (
                  <EmployeeCard key={emp.employeeId} emp={emp} period={period} />
                ))}
              </div>
            )}
          </>
        )}

        <footer style={{ marginTop: 48, textAlign: "center", fontSize: 12, color: "#d1d5db" }}>
          WorkTrack · FY 2026–27 · {FY_START} → {FY_END}
        </footer>
      </main>
    </div>
  );
};

export default HrAdminView;